// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { linksFieldDefinitions, mapLinks } from "@pacific/Overrides/Widgets/Common/HeaderSignIn";
import { color, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    orderManagementSectionTitle = "orderManagementSectionTitle",
    orderManagementLinks = "orderManagementLinks",
    accountManagementTitle = "accountManagementTitle",
    accountManagementLinks = "accountManagementLinks",
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const orderManagementLinks = mapLinks(state, ownProps.fields.orderManagementLinks);
    const accountManagementLinks = mapLinks(state, ownProps.fields.accountManagementLinks);
    const linkGroups = [
        { title: ownProps.fields.accountManagementTitle, links: accountManagementLinks },
        { title: ownProps.fields.orderManagementSectionTitle, links: orderManagementLinks },
    ];
    return {
        linkGroups,
        currentBillTo: getCurrentBillToState(state).value,
        session: getSession(state),
    };
};

export interface MyAccountLinkGroupsStyles {
    wrapper?: InjectableCss;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
}

export const myAccountLinkGroupsStyles: MyAccountLinkGroupsStyles = {
    wrapper: {
        css: css`
            ${xScreen("lt", "lg")} {
                display: flex;
                justify-content: space-between;
                column-gap: 14px;
                margin-bottom: 5px;
            }

            ${xScreen("lte", "xs")} {
                flex-direction: column;
                margin: 0;
            }
        `,
    },
    linkItems: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                margin-bottom: 8px;
                ${xScreen("lt", "lg")} {
                    width: 50%;
                    height: fit-content;
                }
                ${xScreen("lte", "xs")} {
                    width: 100%;
                }
            `,
        },
        title: {
            variant: "h6",
            css: css`
                flex-basis: 100%;
                padding-bottom: 7px;
                border-bottom: 1px solid ${color("common.border")};
                margin-bottom: 11px;
            `,
        },
        link: {
            color: "text.main",
            typographyProps: {
                size: 12,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    margin-bottom: 12px;
                `,
            },
        },
        linkWrapper: {
            css: css`
                flex-basis: calc(50% - 8px);
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.orderManagementSectionTitle]: string;
        [fields.orderManagementLinks]: LinkModel[];
        [fields.accountManagementTitle]: string;
        [fields.accountManagementLinks]: LinkModel[];
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = myAccountLinkGroupsStyles;

export const MyAccountLinkGroups = ({ linkGroups, currentBillTo, session }: Props) => {
    const _GaMyAccountEvents = (event?: string, title?: string, url?: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer || !event) {
            return;
        }
        const googleAnalyticsData = {
            event,
            click_text: title,
            destination: url,
            authentication_state: "Authenticated",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {linkGroups?.map((linkGroup, index) => {
                if (linkGroup.links.filter(n => n !== undefined).length > 0) {
                    return (
                        <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                            {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                            {linkGroup.links.map(
                                link =>
                                    link?.url && (
                                        <StyledWrapper
                                            key={`${link?.title}_${link?.url}`}
                                            {...styles.linkItems?.linkWrapper}
                                        >
                                            <Link
                                                href={link?.url}
                                                onClick={() =>
                                                    _GaMyAccountEvents(link?.gaEvent, link?.title, link?.url)
                                                }
                                                target={link.openInNewWindow ? "_blank" : ""}
                                                {...styles.linkItems?.link}
                                                aria-label={link?.title}
                                            >
                                                {link?.title}
                                            </Link>
                                        </StyledWrapper>
                                    ),
                            )}
                        </StyledWrapper>
                    );
                }
                return null;
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(MyAccountLinkGroups),
    definition: {
        displayName: "My Account Link Groups",
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        fieldDefinitions: linksFieldDefinitions as FieldDefinition[],
    },
};

export default widgetModule;
